// AB#2130 — this is the router-config module, not a fast-refreshable component file: it already exports
// the non-component `router`, and lazy route components are declared here as module-level consts
// (lazyRouteComponent bindings). The react-refresh/only-export-components rule is therefore N/A — disable
// it file-wide (the same pattern used by the tenant app's Router.tsx).
/* eslint-disable react-refresh/only-export-components */
import { createRootRoute, createRoute, createRouter, lazyRouteComponent } from '@tanstack/react-router'
import { RootLayout } from './components/RootLayout'
import { RouteErrorFallback } from './components/RouteErrorFallback'
import { LoginPage } from './pages/Login'
import { DashboardPage } from './pages/Dashboard'
import { TenantsPage } from './pages/Tenants'
import { NewTenantPage } from './pages/NewTenant'
import { TenantDetailLayout } from './pages/tenant-detail/TenantDetailLayout'
import { OverviewTab } from './pages/tenant-detail/OverviewTab'
import { SmtpSettingsPage } from './pages/SmtpSettings'
import { RoleTemplatesPage } from './pages/RoleTemplates'
import { BundlesPage } from './pages/Bundles'
import { ModulesPage } from './pages/Modules'

// AB#2130 — tenant-detail sub-pages are code-split: each tab is its own chunk, fetched on first
// visit. lazyRouteComponent's second arg is the module's export name (all our pages use a named
// export, not `default`); a genuine chunk-load failure rethrows into the router's
// defaultErrorComponent below rather than crashing the shell.
const RalphTab = lazyRouteComponent(() => import('./pages/tenant-detail/RalphTab'), 'RalphTab')
const CompaniesTab = lazyRouteComponent(() => import('./pages/tenant-detail/CompaniesTab'), 'CompaniesTab')
const ModulesTab = lazyRouteComponent(() => import('./pages/tenant-detail/ModulesTab'), 'ModulesTab')
const IdentityTab = lazyRouteComponent(() => import('./pages/tenant-detail/IdentityTab'), 'IdentityTab')
const DiagnosticsTab = lazyRouteComponent(() => import('./pages/tenant-detail/DiagnosticsTab'), 'DiagnosticsTab')
const LifecycleTab = lazyRouteComponent(() => import('./pages/tenant-detail/LifecycleTab'), 'LifecycleTab')

const rootRoute = createRootRoute({ component: RootLayout })

const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: DashboardPage,
})

const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginPage,
})

const tenantsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tenants',
    component: TenantsPage,
})

const newTenantRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tenants/new',
    component: NewTenantPage,
})

// AB#2130 — /tenants/$slug is a layout route: the shared tenant query, breadcrumb, header card and
// tab strip live in TenantDetailLayout; each tab is a nested child rendered into its Outlet. The
// index child renders Overview, so bare /tenants/$slug deep links keep working unchanged.
// '/tenants/new' above stays a flat sibling — its static segment ranks above the $slug param.
const tenantDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tenants/$slug',
    component: TenantDetailLayout,
})

const tenantDetailIndexRoute = createRoute({
    getParentRoute: () => tenantDetailRoute,
    path: '/',
    component: OverviewTab,
})

const tenantDetailRalphRoute = createRoute({
    getParentRoute: () => tenantDetailRoute,
    path: 'ralph',
    component: RalphTab,
})

const tenantDetailCompaniesRoute = createRoute({
    getParentRoute: () => tenantDetailRoute,
    path: 'companies',
    component: CompaniesTab,
})

const tenantDetailModulesRoute = createRoute({
    getParentRoute: () => tenantDetailRoute,
    path: 'modules',
    component: ModulesTab,
})

const tenantDetailIdentityRoute = createRoute({
    getParentRoute: () => tenantDetailRoute,
    path: 'identity',
    component: IdentityTab,
})

const tenantDetailDiagnosticsRoute = createRoute({
    getParentRoute: () => tenantDetailRoute,
    path: 'diagnostics',
    component: DiagnosticsTab,
})

const tenantDetailLifecycleRoute = createRoute({
    getParentRoute: () => tenantDetailRoute,
    path: 'lifecycle',
    component: LifecycleTab,
})

const smtpSettingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings/smtp',
    component: SmtpSettingsPage,
})

const roleTemplatesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/role-templates',
    component: RoleTemplatesPage,
})

const bundlesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/bundles',
    component: BundlesPage,
})

const modulesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/modules',
    component: ModulesPage,
})

const stylePresetsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/style-presets', component: lazyRouteComponent(() => import('./pages/StylePresets'), 'StylePresetsPage') })

const routeTree = rootRoute.addChildren([
    indexRoute,
    loginRoute,
    tenantsRoute,
    newTenantRoute,
    tenantDetailRoute.addChildren([
        tenantDetailIndexRoute,
        tenantDetailRalphRoute,
        tenantDetailCompaniesRoute,
        tenantDetailModulesRoute,
        tenantDetailIdentityRoute,
        tenantDetailDiagnosticsRoute,
        tenantDetailLifecycleRoute,
    ]),
    smtpSettingsRoute,
    stylePresetsRoute,
    roleTemplatesRoute,
    bundlesRoute,
    modulesRoute,
])

// AB#2130 — route-level error containment (the tenant app's AB#2044 pattern): a throw in any
// route's render shows the fallback card in place of that route while the shell/navigation stay
// alive. Added alongside the app's first lazy chunks so a failed chunk load degrades gracefully;
// the router's CatchBoundary is keyed on state.loadedAt so the error resets on navigation.
export const router = createRouter({
    routeTree,
    defaultErrorComponent: RouteErrorFallback,
})

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router
    }
}
